import React, {useCallback, useEffect, useState} from "react";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../services/GraphQLServices";
import {
	Box,
	Breadcrumbs, Button, Checkbox,
	CircularProgress, LinearProgress, Stack, TextField,
	Typography
} from "@mui/material";
import {deepClone, normalizeData} from "../../libraries/util/Functions";
import {PentaSplit} from "../../components/common/PentaSplit";
import {Link} from "react-router-dom";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {
	ANALYZE_CHANGES,
	PUT_COMPARATOR_CONFIG, PUT_CUSTOM_ATTRIBUTES_SETUP, PUT_PARTNER_HIERARCHY,
	PUT_PRODUCT, PUT_PRODUCT_COMMISSION, PUT_PRODUCT_INCENTIVE_DETAILS,
	PUT_PRODUCT_SUPPLIER,
} from "./Queries";
import {DifferenceSet} from "./models/DifferenceSet";
import {ProductSupplier} from "./models/ProductSupplier";
import {Product} from "./models/Product";
import {cloneMutationData} from "./Utils";
import {openProductSupplierDetailsFor} from "./suppliers/SuppliersDetails";
import {DeployDisplay} from "../../components/common/deploy/DeployDisplay";
import {ComparatorConfig} from "./models/ComparatorConfig";
import {openComparatorConfigDetailsFor} from "./comparator-configs/ComparatorConfigsDetails";
import {openProductDetailsFor} from "./products-details/ProductsDetails";
import {ProductIncentiveDetails} from "./models/ProductIncentiveDetails";
import {openProductIncentiveDetailsFor, openProductIncentiveDetailsForNew} from "./products-Incentive/IncentiveDetails";
import {ProductCommission} from "./models/ProductCommission";
import {translateFunction} from "../../localization/TranslationService";
import {PartnerHierarchy} from "./models/PartnerHierarchy";
import {openPartnerHierarchyDetailsFor} from "./partner-hierarchy/PartnerHierarchyDetails";
import {CustomAttributeSetup} from "./models/CustomAttributeSetup";
import {linkCustomAttributeSetupDetailsFor} from "./custom-attribute-setup/CustomAttributeSetupDetails";

const translate = translateFunction("SureBasePortalProductsCatalog");

async function putSupplier(stage: string, item: ProductSupplier) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PRODUCT_SUPPLIER,
		variables: {
			stage: stage,
			productSupplier: cloneMutationData(item),
		}
	});
}

async function deleteSupplier(stage: string, itemId: string) {
	// todo
}

function openNewSupplierDetails(id: string) {
	// todo
}

async function putProduct(stage: string, item: Product) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PRODUCT,
		variables: {
			stage: stage,
			product: cloneMutationData(item),
		}
	});
}

async function deleteProduct(stage: string, itemId: string) {
	// todo
}

function openNewProductDetails(id: string) {
	// todo
}

async function putComparatorConfig(stage: string, item: ComparatorConfig) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_COMPARATOR_CONFIG,
		variables: {
			stage: stage,
			comparatorConfig: cloneMutationData(item),
		}
	});
}

async function deleteComparatorConfig(stage: string, itemId: string) {
	// todo
}

function openNewComparatorConfigDetails(id: string) {
	// todo
}

async function putProductIncentive(stage: string, item: ProductIncentiveDetails) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PRODUCT_INCENTIVE_DETAILS,
		variables: {
			stage: stage,
			productIncentiveDetails: cloneMutationData(item),
		}
	});
}

async function deleteProductIncentive(stage: string, itemId: string) {
	// todo
}

async function putProductCommission(stage: string, item: ProductCommission) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PRODUCT_COMMISSION,
		variables: {
			stage: stage,
			productCommission: cloneMutationData(item),
		}
	});
}


async function putPartnerHierarchy(stage: string, item: PartnerHierarchy) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PARTNER_HIERARCHY,
		variables: {
			stage: stage,
			partnerHierarchy: cloneMutationData(item),
		}
	});
}

async function putCustomAttributeSetup(stage: string, item: CustomAttributeSetup) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_CUSTOM_ATTRIBUTES_SETUP,
		variables: {
			stage: stage,
			customAttributeSetup: cloneMutationData(item)
		}
	});
}

async function deleteProductCommission(stage: string, itemId: string) {
	// todo
}

function openProductCommissionDetailsFor(itemId: string) {
	openProductDetailsFor(itemId, "commission");
}

function openPartnerHierarchy(itemId: string) {
	openPartnerHierarchyDetailsFor(itemId);
}

async function deletePartnerHierarchy(stage: string, itemId: string) {
	// todo
}

async function deleteCustomAttributeSetup(stage: string, itemId: string) {
	// todo
}

function openCustomAttributeSetup(itemId: string) {
	linkCustomAttributeSetupDetailsFor(itemId);
}

// async function applyAll(differenceSet: DifferenceSet) {
// 	for (const itemArray of differenceSet.products) {
// 		for (let i = 1; i < itemArray.length; i++) {
// 			const item = itemArray[i];
// 			const previousItem = itemArray[i - 1];
// 			if (!item && previousItem && previousItem.clientName === "dyme") {
// 				await putProduct(differenceSet.stages[i], previousItem);
// 				itemArray[i] = previousItem;
// 			}
// 		}
// 	}
// }
//
// function jsonFormat(value: any): string {
// 	return JSON.stringify(value, null, 4);
// }

interface CheckboxProps {
	id: string;
	checked: boolean;
}

const MuiCheckbox = React.memo(({ id, checked }: CheckboxProps) => {
	return (
		<Checkbox
			id={id}
			checked={checked}
		/>
	);
});

const selectedObject = {
	suppliersParent: false,
	suppliers: [false],
	productsParent: false,
	products: [false],
	comparatorConfigsParent: false,
	comparatorConfigs: [false],
	incentiveDetailsParent: false,
	incentiveDetails: [false],
	productCommissionsParent: false,
	productCommissions: [false],
	partnerHierarchyParent:false,
	partnerHierarchy: [false],
	customAttributeSetupParent:false,
	customAttributeSetup: [false],
	isReady: false
};

const visibleObject = {
	suppliers: [true],
	products: [true],
	comparatorConfigs: [true],
	incentiveDetails: [true],
	productCommissions: [true],
	partnerHierarchy: [true],
	customAttributeSetup: [true]
};

export const ProductsChangesOverview = pageAuthorizedByGroup(function () {
	const query = useQuery(ANALYZE_CHANGES, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache"
	});
	
	const [selected, setSelected] = useState(selectedObject);
	const [visible, setVisible] = React.useState(visibleObject);
	const [progress, setProgress] = React.useState(0);
	
	const [data, setData] = useState<DifferenceSet>({
		stages: [],
		suppliers: [],
		products: [],
		comparatorConfigs: [],
		incentiveDetails: [],
		productCommissions: [],
		partnerHierarchy: [],
		customAttributeSetup: []
	});
	
	useEffect(() => {
		if (query.data) {
			const differenceSet: DifferenceSet = normalizeData(deepClone(query.data.analyzeChanges));
			setData(differenceSet);
			setSelected(prevState => {
				const newSuppliers = new Array(differenceSet.suppliers.length).fill(false);
				const newProducts = new Array(differenceSet.products.length).fill(false);
				const newComparatorConfigs = new Array(differenceSet.comparatorConfigs.length).fill(false);
				const newIncentiveDetails = new Array(differenceSet.incentiveDetails.length).fill(false);
				const newProductCommissions = new Array(differenceSet.productCommissions.length).fill(false);
				const newPartnerHierarchy = new Array(differenceSet.partnerHierarchy.length).fill(false);
				const newCustomAttributeSetup = new Array(differenceSet.customAttributeSetup.length).fill(false);
					
				return {
					...prevState,
					suppliers: newSuppliers,
					products: newProducts,
					comparatorConfigs: newComparatorConfigs,
					incentiveDetails: newIncentiveDetails,
					productCommissions: newProductCommissions,
					partnerHierarchy: newPartnerHierarchy,
					customAttributeSetup: newCustomAttributeSetup,
					isReady: true
				};
			});
			
			setVisible(prevState => {
				return {
					...prevState,
					suppliers: new Array(differenceSet.suppliers.length).fill(true),
					products: new Array(differenceSet.products.length).fill(true),
					comparatorConfigs: new Array(differenceSet.comparatorConfigs.length).fill(true),
					incentiveDetails: new Array(differenceSet.incentiveDetails.length).fill(true),
					productCommissions: new Array(differenceSet.productCommissions.length).fill(true),
					partnerHierarchy: new Array(differenceSet.partnerHierarchy.length).fill(true),
					customAttributeSetup: new Array(differenceSet.customAttributeSetup.length).fill(true)
				};
			});
		}
	}, [query.data]);
	
	const changeHandler = useCallback((event: any) => {
		const id = event.target.id;
		const parts = id.split("-");
		const section = parts[1];
		const index = parts[2];
		
		if  (!["suppliers", "products", "comparatorConfigs", "incentiveDetails",
			"productCommissions", "partnerHierarchy", "partnerHierarchy",
			"customAttributeSetup"].includes(section)) {
			return;
		}
		
		type SelectedObjectKeys = keyof typeof selectedObject;
		const selectedKey: SelectedObjectKeys = section;
		
		setSelected(prevState => {
			let updatedArray = [...(prevState[selectedKey] as boolean[])];
			if (index == null) {
				updatedArray = updatedArray.map(_ => event.target.checked);
				return {
					...prevState,
					[section]: updatedArray,
					[`${section}Parent`]: event.target.checked
				};
			} else {
				updatedArray[index] = event.target.checked;
				return {
					...prevState,
					[section]: updatedArray,
					[`${section}Parent`]: updatedArray.indexOf(false) === -1
				};
			}
		});
	}, []);
	
	if (query.loading || !selected.isReady) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (query.error) {
		throw query.error;
	}
	
	if (!query.data) {
		throw new Error("Missing data!");
	}
	
	// async function applyAll(differenceSet: DifferenceSet) {
	// 	for (const itemArray of differenceSet.productCommissions) {
	// 		for (let i = 1; i < itemArray.length; i++) {
	// 			const item = itemArray[i];
	// 			if (item !== null) {
	// 				const previousItem = itemArray[i - 1];
	// 				if (!item && previousItem) {
	// 					await putProductCommission(differenceSet.stages[i], previousItem);
	// 					itemArray[i] = previousItem;
	// 				} else if (item && previousItem && jsonFormat(item) !== jsonFormat(previousItem)) {
	// 					await putProductCommission(differenceSet.stages[i], previousItem);
	// 					itemArray[i] = previousItem;
	// 				}
	// 			}
	// 		}
	// 	}
	// }
	//
	// function jsonFormat(value: any): string {
	// 	return JSON.stringify(value, null, 4);
	// }
	//
	// async function handleApplyAll(differenceSet: DifferenceSet) {
	// 	await applyAll(differenceSet);
	// 	await query.refetch();
	// }
	
	function jsonFormat(value: any): string {
		return JSON.stringify(value, null, 4);
	}
	
	async function updateItem(stage: string, previousItem: any, property: string) {
		switch (property) {
			case "customAttributeSetup":
				await putCustomAttributeSetup(stage, previousItem);
				break;
			case "incentiveDetails":
				await putProductIncentive(stage, previousItem);
				break;
			case "partnerHierarchy":
				await putPartnerHierarchy(stage, previousItem);
				break;
			case "products":
				await putProduct(stage, previousItem);
				break;
			case "comparatorConfigs":
				await putComparatorConfig(stage, previousItem);
				break;
			case "suppliers":
				await putSupplier(stage, previousItem);
				break;
			case "productCommissions":
				await putProductCommission(stage, previousItem);
				break;
		}
	}
	
	async function advanceItem(itemArray: any, property: string) {
		if (itemArray == null)
			return;
		
		for (let i = 1; i < itemArray.length; i++) {
			const item = itemArray[i];
			const previousItem = itemArray[i - 1];
			if (item == null && previousItem != null) {
				await updateItem(data.stages[i], previousItem, property);
				itemArray[i] = previousItem;
				break; // We only want to update one level at a time
			} else if (item != null && previousItem != null && jsonFormat(item) !== jsonFormat(previousItem)) {
				await updateItem(data.stages[i], previousItem, property);
				itemArray[i] = previousItem;
				break; // We only want to update one level at a time
			}
		}
	}
	
	async function bulkAdvance() {
		const keys = Object.keys(data) as Array<keyof typeof data>;
		
		let total = 0, currentIndex = 0;
		
		for (const key of keys) {
			if (key === "stages") continue;
			const selectedObject = selected[key];
			total += selectedObject.filter(i => i).length;
		}
		
		for (const key of keys) {
			if (key === "stages") continue;
			
			const selectedObj = selected[key];
			if (selectedObj.every(i => !i))
				continue;
			
			const visibleObj = visible[key];
			if (visibleObj.every(i => !i))
				continue;
			
			const obj = data[key];
			let index = 0;
			
			for (const itemArr of obj) {
				if (!itemArr || !selectedObj[index] || !visibleObj[index]) {
					index++;
					currentIndex++;
					continue;
				}
				await advanceItem(itemArr, key);
				index++;
				currentIndex++;
				if (currentIndex % 10 === 0 || total < 10) {
					setProgress((currentIndex / total) * 100);
				}
			}
		}
		
		window.location.reload();
	}
	
	function containsString(obj: any, searchString: string): boolean {
		if (typeof obj === "string") {
			return obj.includes(searchString);
		} else if (typeof obj === "object" && obj !== null) {
			for (const key in obj) {
				if (containsString(obj[key], searchString)) {
					return true;
				}
			}
		}
		return false;
	}
	
	function filterVisible(krnSegment: any) {
		
		setSelected(prevState => {
			return {
				...prevState,
				suppliers: new Array(prevState.suppliers.length).fill(false),
				products: new Array(prevState.products.length).fill(false),
				productCommissions: new Array(prevState.productCommissions.length).fill(false),
				incentiveDetails: new Array(prevState.incentiveDetails.length).fill(false),
				customAttributeSetup: new Array(prevState.customAttributeSetup.length).fill(false),
				partnerHierarchy: new Array(prevState.partnerHierarchy.length).fill(false),
				comparatorConfigs: new Array(prevState.comparatorConfigs.length).fill(false),
				productsParent: false,
				customAttributeSetupParent: false,
				partnerHierarchyParent: false,
				productCommissionsParent: false,
				incentiveDetailsParent: false,
				suppliersParent: false,
				comparatorConfigsParent: false
			};
		});
		
		setVisible(prevState => {
			
			const newArrays = {
				stages: [] as boolean[],
				suppliers: [] as boolean[],
				products: [] as boolean[],
				comparatorConfigs: [] as boolean[],
				incentiveDetails: [] as boolean[],
				productCommissions: [] as boolean[],
				customAttributeSetup: [] as boolean[],
				partnerHierarchy: [] as boolean[]
			};
			
			type SelectedArrayKeys = keyof typeof newArrays;
			
			const keys = Object.keys(data) as Array<keyof typeof data>;
			for (const key of keys) {
				const selectedKey: SelectedArrayKeys = key;
				let index = 0;
				const itemArr = data[key];
				for (const item of itemArr) {
					newArrays[selectedKey][index] = containsString(item[0], krnSegment);
					index++;
				}
			}
			
			return {
				...prevState,
				suppliers: newArrays.suppliers,
				products: newArrays.products,
				comparatorConfigs: newArrays.comparatorConfigs,
				incentiveDetails: newArrays.incentiveDetails,
				productCommissions: newArrays.productCommissions,
				customAttributeSetup: newArrays.customAttributeSetup,
				partnerHierarchy: newArrays.partnerHierarchy
			};
		});
	}
	
	let timeout: any;
	
	function filterChanged(event: any) {
		if (timeout != null)
			return;
		timeout = setTimeout(() => {
			filterVisible(event.target.value);
			clearTimeout(timeout);
			timeout = null;
		}, 2000);
	}
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{("Products")}</Link>
			<Typography color="text.primary">{translate("Changes")}</Typography>
		</Breadcrumbs>}
		center={<div style={{overflowY: "auto", height: "100%"}} onChange={changeHandler}>
			{/*<ProcessButton*/}
			{/*	variant={"contained"}*/}
			{/*	defaultText={translate("Apply All commissions")}*/}
			{/*	inProgressText={translate("Applying...")}*/}
			{/*	completedText={translate("Changes applied")}*/}
			{/*	onClick={() => handleApplyAll(differenceSet)}*/}
			{/*/>*/}
			{data.suppliers.length > 0 &&
				<Stack direction={"row"} display={visible.suppliers.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-suppliers"} checked={selected.suppliersParent} ></MuiCheckbox>
					<Typography>{translate("Suppliers")}</Typography>
				</Stack>
			}
			
			{data.suppliers.map((set, index) => {
				return <Stack direction="row" display={visible.suppliers[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`productsupplierStack-${index}`}>
					<MuiCheckbox id={`cb-suppliers-${index}`} checked={selected.suppliers[index]}></MuiCheckbox>
					<DeployDisplay<ProductSupplier>
						name={translate("Product Supplier")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.krn}
						key={`productsupplier-${index}`}
						putItem={putSupplier}
						deleteItem={deleteSupplier}
						openItemDetails={openProductSupplierDetailsFor}
						openNewItemDetails={openNewSupplierDetails}
					/>
				</Stack>;
			})}
			
			{data.products.length > 0 &&
				<Stack direction={"row"} display={visible.products.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-products"} checked={selected.productsParent}></MuiCheckbox>
					<Typography>{translate("Products")}</Typography>
				</Stack>
			}
			{data.products.map((set, index) => {
				return <Stack direction="row" display={visible.products[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`productStack-${index}`}>
					<MuiCheckbox id={`cb-products-${index}`} checked={selected.products[index]}></MuiCheckbox>
					<DeployDisplay<Product>
						name={translate("Product")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.krn}
						key={`product-${index}`}
						putItem={putProduct}
						deleteItem={deleteProduct}
						openItemDetails={openProductDetailsFor}
						openNewItemDetails={openNewProductDetails}
					/>
				</Stack>;
			})}
			
			
			{data.comparatorConfigs.length > 0 &&
				<Stack direction={"row"} display={visible.comparatorConfigs.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-comparatorConfigs"} checked={selected.comparatorConfigsParent}></MuiCheckbox>
					<Typography>{translate("Comparator Configs")}</Typography>
				</Stack>
			}
			{data.comparatorConfigs.map((set, index) => {
				return <Stack direction="row" display={visible.comparatorConfigs[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`comparatorConfigStack-${index}`}>
					<MuiCheckbox id={`cb-comparatorConfigs-${index}`} checked={selected.comparatorConfigs[index]}></MuiCheckbox>
					<DeployDisplay<ComparatorConfig>
						name={translate("Comparator Config")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.krn}
						key={`comparatorConfig-${index}`}
						putItem={putComparatorConfig}
						deleteItem={deleteComparatorConfig}
						openItemDetails={openComparatorConfigDetailsFor}
						openNewItemDetails={openNewComparatorConfigDetails}
					/>
				</Stack>;
			})}
			
			{data.incentiveDetails.length > 0 &&
				<Stack direction={"row"} display={visible.incentiveDetails.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-incentiveDetails"} checked={selected.incentiveDetailsParent}></MuiCheckbox>
					<Typography>{translate("Product Incentives")}</Typography>
				</Stack>
			}
			{data.incentiveDetails.map((set, index) => {
				return <Stack direction="row" display={visible.incentiveDetails[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`productIncentiveDetailsStack-${index}`}>
					<MuiCheckbox id={`cb-incentiveDetails-${index}`} checked={selected.incentiveDetails[index]}></MuiCheckbox>
					<DeployDisplay<ProductIncentiveDetails>
						name={translate("Product Incentive")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.krn}
						key={`productIncentiveDetails-${index}`}
						putItem={putProductIncentive}
						deleteItem={deleteProductIncentive}
						openItemDetails={openProductIncentiveDetailsFor}
						openNewItemDetails={openProductIncentiveDetailsForNew}
					/>
				</Stack>;
			})}
			
			{data.productCommissions.length > 0 &&
				<Stack direction={"row"} display={visible.productCommissions.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-productCommissions"} checked={selected.productCommissionsParent}></MuiCheckbox>
					<Typography>{translate("Product Commissions")}</Typography>
				</Stack>
			}
			{data.productCommissions.map((set, index) => {
				return <Stack direction="row" display={visible.productCommissions[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`productCommissionsStack-${index}`}>
					<MuiCheckbox id={`cb-productCommissions-${index}`} checked={selected.productCommissions[index]}></MuiCheckbox>
					<DeployDisplay<ProductCommission>
						name={translate("Product Commission")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.krn}
						key={`productCommissions-${index}`}
						putItem={putProductCommission}
						deleteItem={deleteProductCommission}
						openItemDetails={openProductCommissionDetailsFor}
						openNewItemDetails={openProductCommissionDetailsFor}
					/>
				</Stack>;
			})}
			
			{data.partnerHierarchy.length > 0 &&
				<Stack direction={"row"} display={visible.partnerHierarchy.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-partnerHierarchy"} checked={selected.partnerHierarchyParent}></MuiCheckbox>
					<Typography>{translate("Partner Hierarchy")}</Typography>
				</Stack>
			}
			{data.partnerHierarchy.map((set, index) => {
				return <Stack direction="row" display={visible.partnerHierarchy[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`putPartnerHierarchyStack-${index}`}>
					<MuiCheckbox id={`cb-partnerHierarchy-${index}`} checked={selected.partnerHierarchy[index]}></MuiCheckbox>
					<DeployDisplay<PartnerHierarchy>
						name={translate("Partner Hierarchy")}
						stages={data.stages}
						items={set}
						keyMapper={it => it.id}
						key={`putPartnerHierarchy-${index}`}
						putItem={putPartnerHierarchy}
						deleteItem={deletePartnerHierarchy}
						openItemDetails={openPartnerHierarchy}
						openNewItemDetails={openPartnerHierarchy}
					/>
				</Stack>;
			})}
			
			{data.customAttributeSetup.length > 0 &&
				<Stack direction={"row"} display={visible.customAttributeSetup.every(i => !i) ? "none": "flex"}
					alignItems={"center"}>
					<MuiCheckbox id={"cb-customAttributeSetup"} checked={selected.customAttributeSetupParent}></MuiCheckbox>
					<Typography>{translate("Custom Attribute Setup")}</Typography>
				</Stack>
			}
			{data.customAttributeSetup.map((set, index) => {
				return <Stack direction="row" display={visible.customAttributeSetup[index] ? "flex" : "none"}
					style={{borderTop: "1px solid whitesmoke"}} key={`putCustomAttributeSetupStack-${index}`}>
					<MuiCheckbox id={`cb-customAttributeSetup-${index}`} checked={selected.customAttributeSetup[index]}></MuiCheckbox>
					<DeployDisplay<CustomAttributeSetup>
						name={translate("Custom Attributes Setup")}
						stages={data.stages}
						items={set}
						labelMapper={x => x.description}
						keyMapper={it => it.id}
						key={`putCustomAttributeSetup-${index}`}
						putItem={putCustomAttributeSetup}
						deleteItem={deleteCustomAttributeSetup}
						openItemDetails={openCustomAttributeSetup}
						openNewItemDetails={openCustomAttributeSetup}
					/>
				</Stack>;
			})}
		</div>}
		bottom={
			<Stack direction={"row"}>
				<Button 
					variant={"contained"}
					style={{marginLeft: 3}} onClick={bulkAdvance}>{translate("Bulk Advance")}
				</Button>
				<TextField
					variant={"outlined"}
					label={translate("Filter")}
					onChange={filterChanged}
					sx={{
						"& .MuiOutlinedInput-root": {
							height: "40px",
							alignItems: "center"
						},
						"& .MuiInputLabel-root": {
							transform: "translate(14px, 10px) scale(1)",
						},
						"& .MuiInputLabel-shrink": {
							transform: "translate(14px, -6px) scale(0.75)",
						}
					}}
					style={{marginLeft: 5}}
				/>
				<LinearProgress variant={"determinate"} value={progress}
					style={{width: 150, height:40, marginLeft:5, alignSelf:"center"}}></LinearProgress>
			</Stack>
		}
	/>;
}, ["Admin", "Product"]);
